import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabGroup } from '@angular/material/tabs';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LetDirective } from '@ngrx/component';
import { provideComponentStore } from '@ngrx/component-store';

import { LinkedItemTypeEnum } from '@ninety/platform/models/linked-items/linked-item-type-enum';
import { TerraCheckboxModule, TerraModule } from '@ninety/terra';
import {
  StopPropagationDirective,
  ButtonComponent,
  NotifyIntersectDirective,
  LegacyAlertComponent as AlertComponent,
} from '@ninety/ui/core';

import { LinkedExistingItemsStore } from './linked-existing-items.store';

export type LinkedExistingItemsDialogData = {
  fromId: string;
  linkedItemType: LinkedItemTypeEnum;
  title: string;
  teamId: string;
  openDialogWithItemTypeTabSelected: boolean;
  selectedLinkedItemTabType?: LinkedItemTypeEnum;
};
@Component({
  selector: 'ninety-linked-existing-items-dialog',
  templateUrl: './linked-existing-items-dialog.component.html',
  styleUrls: ['./linked-existing-items-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatToolbarModule,
    FormsModule,
    TerraModule,
    LetDirective,
    NotifyIntersectDirective,
    TerraCheckboxModule,
    ButtonComponent,
    StopPropagationDirective,
    AlertComponent,
  ],
  providers: [provideComponentStore(LinkedExistingItemsStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkedExistingItemsDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  dialogRef = inject<MatDialogRef<LinkedExistingItemsDialogComponent>>(MatDialogRef);
  data = inject<LinkedExistingItemsDialogData>(MAT_DIALOG_DATA);

  protected store = inject(LinkedExistingItemsStore);

  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;

  constructor() {
    const data = this.data;

    this.store.setFromLinkedItem({
      fromLinkedItem: { id: data.fromId, type: data.linkedItemType },
      fromLinkedItemTitle: data.title,
      fromLinkedItemTeamId: data.teamId,
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  ngOnInit() {
    if (this.data.openDialogWithItemTypeTabSelected && this.data.selectedLinkedItemTabType) {
      this.store.onSelectedIndexChanged(this.getSelectedTabIndex());
    }
    this.store.fetchMore();
  }

  ngAfterViewInit() {
    if (this.data.openDialogWithItemTypeTabSelected && this.data.selectedLinkedItemTabType) {
      // when we programmatically select the label is sometimes cut off by the pagination
      this.tabGroup._tabHeader._scrollToLabel(this.getSelectedTabIndex());
    }
  }

  private getSelectedTabIndex(): number {
    // Fall back to the first tab when the requested type isn't shown (e.g. milestones disabled)
    return Math.max(this.store.getTabIndex(this.data.selectedLinkedItemTabType), 0);
  }

  trackBy(_, item) {
    return item._id;
  }

  createLinkedItems(): void {
    this.store.createLinkedItems();
    this.dialogRef.close();
  }

  fetchMore(event: boolean) {
    if (event) {
      this.store.fetchMore();
    }
  }
}
